import { useCallback, useEffect, useState } from 'react';
import { Box, Container, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import PersonalTestingStation from '../../components/athletes/PersonalTestingStation';
import { getAthlete, fetchTestTypes, listTestingSessions } from '../../services/athleteService';
import { recordPersonalTestAttempt, endPersonalTestOccurrenceEarly } from '../../services/personalTestingService';
import { filterToCuratedTestTypes } from '../../utils/curatedTestTypes';

// Personal Testing Station — thin, router-aware page wrapper, same
// discipline as TeamTestingPage.js: resolves route params + loads data,
// then hands everything to the pure, router-free PersonalTestingStation
// component as plain injected props/callbacks.
//
// Authorization: getAthlete() itself only succeeds under existing RLS
// for an active guardian of this athlete (or platform admin) — the same
// gate AthleteProfilePage.js already relies on. A non-guardian visiting
// this URL directly gets the same load failure they'd get visiting the
// Profile itself; record_personal_test_attempt() (053) independently
// re-verifies the same guardian relationship server-side regardless of
// what this page believes.
//
// Custom Test Types (056) — Team Testing now loads a Program's own live
// enabled catalog (testTypeService.js), but Personal Testing deliberately
// keeps this same fixed, curated four-key list (utils/curatedTestTypes.js)
// unchanged: Personal Testing is Career-wide/athlete-centric, while
// Program custom tests are Program-scoped, and inferring "which Program"
// from current/historical/multiple memberships deserves its own
// deliberate product decision rather than being guessed here. Not a
// technical limitation of the canonical test_types catalog itself.
function PersonalTestingInner() {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [testTypes, setTestTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [a, types] = await Promise.all([getAthlete(athleteId), fetchTestTypes()]);
        setAthlete(a);
        setTestTypes(filterToCuratedTestTypes(types));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [athleteId]);

  const fetchAthleteSessions = useCallback((id) => listTestingSessions(id), []);
  const recordAttempt = useCallback((params) => recordPersonalTestAttempt(params), []);
  // Testing Occurrence / Attempt Protocol (059) — same injected-callback
  // shape as recordAttempt above; getAthlete()'s own guardian-only RLS
  // gate is what actually lets this page load at all, and
  // end_test_occurrence_early() independently re-verifies the same
  // guardian relationship server-side regardless of what this page does.
  const handleEndTestEarly = useCallback((params) => endPersonalTestOccurrenceEarly(params), []);

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  if (!athlete) return null;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Button
        onClick={() => navigate(`/athletes/${athleteId}`, { state: { tab: 1 } })}
        sx={{ textTransform: 'none', color: '#6a8aa8', mb: 1, pl: 0 }}
      >
        ← {athlete.first_name}'s Profile
      </Button>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>Test Yourself</Typography>
      <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', mb: 3 }}>
        Personal testing for {athlete.first_name} — part of their own history, separate from official team results.
      </Typography>
      {testTypes.length === 0 ? (
        <Alert severity="info">No active test types are configured yet.</Alert>
      ) : (
        <PersonalTestingStation
          athlete={athlete}
          testTypes={testTypes}
          fetchAthleteSessions={fetchAthleteSessions}
          recordAttempt={recordAttempt}
          onEndTestEarly={handleEndTestEarly}
        />
      )}
    </Container>
  );
}

export default function PersonalTestingPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><PersonalTestingInner /></RequireAuth>
    </Box>
  );
}
